import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { searchMovies, sortMovies } from "@/utils/movieHelpers";

const typeFilters = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => {
    const base = searchMovies(movies, query);
    const type = searchParams.get("type") ?? "all";
    const filtered = type === "all" ? base : base.filter((m) => m.type === type);
    return sortMovies(filtered, "popular");
  }, [query, searchParams]);

  const hotMovies = useMemo(
    () => [...movies].sort((a, b) => b.views - a.views).slice(0, 4),
    []
  );

  const activeType = searchParams.get("type") ?? "all";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    const next = new URLSearchParams(searchParams);
    if (kw) {
      next.set("q", kw);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  const handleType = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") {
      next.delete("type");
    } else {
      next.set("type", value);
    }
    setSearchParams(next);
  };

  const pageTitle = query ? `${query} - 搜索结果 - 肮脏的交易` : "影视搜索 - 肮脏的交易影视资讯";

  return (
    <>
      <Seo
        title={pageTitle}
        description={
          query
            ? `在肮脏的交易搜索“${query}”的影视作品，获取剧情介绍与角色分析。`
            : "搜索影视作品，获取最新影视资讯、剧情介绍与角色分析。"
        }
        keywords="影视搜索,搜索影视作品,剧情介绍,肮脏的交易"
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">
            {query ? `“${query}”的搜索结果` : "影视搜索"}
          </h1>
          <form
            onSubmit={handleSubmit}
            className="mt-5 flex w-full max-w-2xl items-center rounded-md border border-background-300 bg-background-50 pl-4 pr-1.5 focus-within:border-primary-400 transition-colors"
          >
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视标题、类型或演员关键词"
              className="flex-1 bg-transparent px-3 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {typeFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => handleType(filter.value)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeType === filter.value
                    ? "bg-background-50 text-primary-600"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {query && (
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-primary-600">{results.length}</strong> 部相关作品
            </p>
          )}
        </div>

        {!query ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h3 className="mt-4 text-base font-medium text-foreground-900">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">
              支持按影视标题、类型、演员等关键词进行模糊搜索。
            </p>
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl" />
            </span>
            <h3 className="mt-4 text-base font-medium text-foreground-900">
              没有找到与“{query}”相关的内容
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或者看看下面这些热门影视作品。
            </p>
            <div className="mt-8 w-full max-w-4xl px-4">
              <h4 className="mb-4 text-left text-base font-bold text-foreground-900">
                <Link to="/" className="hover:text-primary-600 cursor-pointer">热门影视推荐</Link>
              </h4>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}